import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector} from "react-redux";

import {service as ContextNodeService} from "../../Services/ContextNodeService";
import {useDebouncedEffect} from "../../Hooks/EffectHooks";
import {ArrayUtils} from "../../Utils/ArrayUtils";
import Select from "react-select/base";
import {usePrimeCache} from "../../Hooks/ServiceHooks";
import {service as ContextNodeTypeService} from "../../Services/ContextNodeTypeService";
import {StreamBankAutoCompleteItem} from "../Common/StreamBankAutoCompleteItem";
import {useFetchContextNode} from "../../Hooks/ContextHooks";

export const SearchBar = props => {
    const context = useSelector(state => state.contextState.context);
    const user = useSelector(state => state.userState.user);
    const editableContextNodeIds = user?.editableContextNodeIds ?? [];

    const [value, setValue] = useState('');
    const [isLoading, setIsLoading] = useState(false)
    const [hasFocus, setHasFocus] = useState(false);
    const [matchedContextNodes, setMatchedContextNodes] = useState(null)
    
    usePrimeCache([ContextNodeTypeService]);
    
    const isOpen = hasFocus;
    
    useEffect(() => {
        setIsLoading(true);
        setMatchedContextNodes(null);
    }, [value, hasFocus]);
    
    useDebouncedEffect(() => {
        if(isOpen) {
            performSearch();
        }
    }, [context, value, isOpen], 250);

    const getPlaceholder = () => {
        if(!context?.parentProgram) {
            return "Programs";
        } else if(!context?.childProgram) {
            return "Sub-Programs";
        } else if(!context?.project) {
            return "Projects";
        } else if(!context?.conservationAction) {
            return "Conservation Actions";
        }

        return "";
    };

    const performSearch = () => {
        ContextNodeService.GetChildrenContextNodes(
            {
                contextNodeId:  context?.contextNodeId,
            },
            value)
            .then((x) => {
                setMatchedContextNodes(x?.result ?? []);
            })
            .catch((e) => {
                setMatchedContextNodes([]);
            });
    };
    
    const currentContextNode = useFetchContextNode(context?.contextNodeId);
    const currentContextNodeType = ContextNodeTypeService.CachedTitleById(currentContextNode?.contextNodeTypeId)
    
    const groupLabelByKey = useMemo(() => {
        const prefix = (currentContextNode?.shortName ?? currentContextNode?.name ?? '');
        return {
            '1': `Programs`,
            '2': `${prefix} Sub-Programs`,
            '3': `${prefix} Projects`,
            '4': `${prefix} Conservation Actions`,
            '5': `${prefix} Sample Events`,
            'other': `From Other Contexts`,
        }
    }, [currentContextNodeType, currentContextNode?.shortName, currentContextNode?.name]);
    
    const contextNodesGroupedByDepth = useMemo(() => {
        if(matchedContextNodes == null) {
            return null;
        }
        
        return ArrayUtils.GroupBy(
            matchedContextNodes,
            (x) => {
                
                if((x?.underCurrentContext ?? true)) {
                    return String(x.depth);
                } else {
                    return 'other';
                }
            },
            (a, b) => editableContextNodeIds.includes(b.contextNodeId) - editableContextNodeIds.includes(a.contextNodeId)
        );
    }, [matchedContextNodes]);
    
    const groupedOptions = useMemo(() => {
        return contextNodesGroupedByDepth?.map((group) => ({
            label: groupLabelByKey[group.key],
            options: group.value.map((item) => ({
                ...item,
                label: item.name,
                value: item.contextNodeId,
                contextNodeType: ContextNodeTypeService.CachedTitleById(item.contextNodeTypeId),
                subtitle: (item?.underCurrentContext ?? true) ? item.nicknames : item.contextDescription,
            }))
        }));
    }, [contextNodesGroupedByDepth, groupLabelByKey]);
    
    useEffect(() => {
        setIsLoading(false);
    }, [groupedOptions]);
    
    const filterOption = useCallback((option, searchValue) => {
        const searchParts = searchValue.trim().toLowerCase().split(/\s+/);
        const optionString = `${option.data.name} ${option.data.shortName} ${option.data.nicknames ?? ''}`.toLowerCase();
        
        return searchParts.every(part => optionString.includes(part));
    }, []);

    const { OnUpdate } = props;
    const placeholder = getPlaceholder();

    //If we're not able to search for anything lets hide this.
    if(placeholder === "") {
        return null;
    }
    
    const formatGroupLabel = (data) => (
        <div className="sb-autocomplete-header-item">
            {data.label}
        </div>
    );
    
    const onChange = (value) => {
        setValue('');
        OnUpdate?.(value);
    }
    
    const formatOptionLabel = (option, formatOptionLabelMeta) => {
        if(formatOptionLabelMeta.context === 'value') {
            return option.label;
        }
        return <StreamBankAutoCompleteItem
            className="sb-search-bar-autocomplete-item"
            isHighlighted={formatOptionLabelMeta.isFocused}
            service={ContextNodeService}
            item={option.item}
            title={option.title}
            subtitle={option.subtitle}
        />
    }
    
    return <Select
            value={null}
            onChange={onChange}
            
            inputValue={value}
            onInputChange={(value, meta) => setValue(value)}
            
            options={groupedOptions}
            
            isClearable={true}
            isSearchable={true}
            placeholder={"Search..."}
            components={{ DropdownIndicator: null }}
            
            isLoading={isLoading}
            loadingMessage={() => "Loading..."}
            
            filterOption={filterOption}
            
            formatGroupLabel={formatGroupLabel}
            formatOptionLabel={formatOptionLabel}
            
            menuIsOpen={isOpen}
            onMenuOpen={() => void setHasFocus(true) }
            onMenuClose={() => void setHasFocus(false)}
            
            styles={{
                option: (provided, state) => ({
                    ...provided,
                    color: '#010101',
                    fontWeight: state.isFocused ? '500' : '200',
                    backgroundColor: state.isFocused ? '#DDDDDD' : 'white',
                }),
                menu: (provided) => ({
                    ...provided,
                    zIndex: 801, /* make sure we are above the Leaflet map components */
                }),
                menuList: (provided) => ({
                    ...provided,
                    paddingTop: '0',
                    borderRadius: '4px',
                }),
                group: (provided) => ({
                    ...provided,
                    paddingTop: '0',
                    paddingBottom: '8px',
                }),
                groupHeading: (provided) => ({
                    ...provided,
                    position: 'sticky',
                    paddingTop: '4px',
                    paddingBottom: '4px',
                    top: 0,
                    // color: 'white',
                    backgroundColor: 'white',
                    borderBottom: '1px solid #e0e0e0',
                    marginBottom: '0',
                }),
            }}
        />;
};


